/**
 * PraticheInCorsoPage — F-005 Phase 5 (Task 5.1).
 *
 * Active-pratiche dashboard for the client. Refined from the sketch
 * `arness-sketches/pratica-status/SketchPraticaStatus.tsx`: the static mock list
 * is replaced with the real `usePratiche` polling hook, and each row renders the
 * shared `StateBadge`.
 *
 * The page composes guard + layout (`RequireAuth` → `AppLayout`) and wraps the
 * inner UI in the feature-scoped `QueryClientProvider`
 * (`praticaStatusQueryClient`). Without that provider the TanStack Query hooks
 * throw "No QueryClient set" — the app has no global QueryClient.
 */
import { QueryClientProvider } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FileText, Loader2, Plus } from 'lucide-react';

import { RequireAuth } from '@/features/auth/components/RequireAuth';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { StateBadge } from '../components/StateBadge';
import { usePratiche } from '../hooks/usePratiche';
import { praticaStatusQueryClient } from '../lib/query-client';

export function PraticheInCorsoPage() {
  return (
    <RequireAuth>
      <AppLayout>
        <QueryClientProvider client={praticaStatusQueryClient}>
          <PraticheInCorsoInner />
        </QueryClientProvider>
      </AppLayout>
    </RequireAuth>
  );
}

function PraticheInCorsoInner() {
  const { data, isLoading, isError } = usePratiche();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-8 text-center text-destructive" role="alert">
        Impossibile caricare le pratiche. Riprova.
      </div>
    );
  }

  const items = data?.items ?? [];

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight">Le tue pratiche</h1>
        <Button asChild>
          <Link to="/app/pratiche/nuova">
            <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Nuova pratica
          </Link>
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="flex flex-col items-center gap-4 py-16 text-center text-muted-foreground">
          <FileText className="h-12 w-12" aria-hidden="true" />
          <p>Nessuna pratica in corso</p>
          <Link
            to="/app/pratiche/nuova"
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            Registra il tuo primo contratto
          </Link>
        </div>
      ) : (
        <ul className="divide-y rounded-lg border">
          {items.map((pratica) => (
            <li key={pratica.id}>
              <Link
                to={`/pratiche/${pratica.id}/stato`}
                className="flex items-center justify-between px-4 py-3 transition-colors hover:bg-muted/40"
              >
                <div className="min-w-0 space-y-0.5">
                  <p className="truncate text-sm font-medium">
                    {pratica.nomeLocatore && pratica.nomeConduttore
                      ? `${pratica.nomeLocatore} → ${pratica.nomeConduttore}`
                      : `Pratica ${pratica.id.slice(0, 8)}`}
                  </p>
                  {pratica.tipoContratto && (
                    <p className="text-xs text-muted-foreground">{pratica.tipoContratto}</p>
                  )}
                </div>
                <StateBadge stato={pratica.stato} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
